import { CURRENCY_CODES, validateSnapshot, convertMajorAmount } from './adasatee-currency-core.js';

const PREFERENCE_KEY = 'adasatee-currency-preference';
const DAY = 86400000;
const COUNTRY_CURRENCY = Object.freeze({KW:'KWD', SA:'SAR', AE:'AED', OM:'OMR', BH:'BHD', IQ:'IQD', QA:'QAR', JO:'JOD'});
const instances = new WeakMap();
const moneySelector = '[data-adasatee-kwd-major]';

function readPreference(storage, now) {
  try {
    const value = JSON.parse(storage?.getItem(PREFERENCE_KEY) ?? 'null');
    if (!value || Object.keys(value).length !== 2 || !CURRENCY_CODES.includes(value.code) ||
      !Number.isSafeInteger(value.expiresAt) || value.expiresAt <= now || value.expiresAt > now + DAY) return null;
    return value.code;
  } catch { return null; }
}

async function fetchPublicSnapshot(url, document) {
  const response = await document.defaultView.fetch(url, {credentials:'omit', redirect:'error'});
  if (!response.ok) throw new Error('Snapshot unavailable');
  return response.json();
}

export function startCurrencyDisplay({document, storage, now = Date.now, fetchSnapshot} = {}) {
  if (instances.has(document)) return instances.get(document);
  const pending = initialize({document, storage, now, fetchSnapshot});
  instances.set(document, pending);
  return pending;
}

async function initialize({document, storage, now, fetchSnapshot}) {
  const native = new WeakMap();
  let snapshot = null;
  let countryCode = 'KW';
  try {
    const config = JSON.parse(document.getElementById('adasatee-currency-config').textContent);
    const url = new URL(config.snapshotUrl, document.location.href);
    if (typeof config.snapshotUrl !== 'string' || !config.snapshotUrl || url.origin !== document.location.origin ||
      !['http:', 'https:'].includes(url.protocol) || url.username || url.password) throw new Error('Invalid public snapshot URL');
    countryCode = config.countryCode;
    snapshot = validateSnapshot(await (fetchSnapshot ? fetchSnapshot(url.href) : fetchPublicSnapshot(url.href, document)));
  } catch { /* Keep exact native KWD markup when config, delivery or validation fails. */ }
  const automatic = COUNTRY_CURRENCY[countryCode] || 'KWD';
  let currencyCode = readPreference(storage, now()) || automatic;
  const observer = new document.defaultView.MutationObserver(() => render());
  const observe = () => observer.observe(document.body, {subtree:true, childList:true, characterData:true, attributes:true, attributeFilter:['data-adasatee-kwd-major']});

  function render() {
    observer.disconnect();
    try {
      for (const node of document.querySelectorAll(moneySelector)) {
        const base = node.getAttribute('data-adasatee-kwd-major');
        let saved = native.get(node);
        if (!saved || saved.lastHtml !== node.innerHTML) {
          saved = {base, html:node.innerHTML, lastHtml:node.innerHTML}; native.set(node, saved);
        }
        if (!snapshot) {
          if (node.innerHTML !== saved.html) node.innerHTML = saved.html;
        } else {
          try {
            const text = `${convertMajorAmount(base, currencyCode, snapshot)} ${currencyCode}`;
            if (node.textContent !== text) node.textContent = text;
          } catch { if (node.innerHTML !== saved.html) node.innerHTML = saved.html; }
        }
        saved.lastHtml = node.innerHTML;
      }
      for (const choice of document.querySelectorAll('[data-adasatee-currency-choice]')) choice.hidden = !snapshot;
      for (const select of document.querySelectorAll('[data-adasatee-currency-select]')) select.value = snapshot ? currencyCode : 'KWD';
      for (const indicator of document.querySelectorAll('[data-adasatee-currency-indicator]')) {
        const text = snapshot ? currencyCode : 'KWD'; if (indicator.textContent !== text) indicator.textContent = text;
      }
      for (const disclosure of document.querySelectorAll('[data-adasatee-currency-disclosure]')) disclosure.hidden = !snapshot || currencyCode === 'KWD';
    } finally { observe(); }
  }
  function setCurrency(code) {
    if (!snapshot || !CURRENCY_CODES.includes(code)) return;
    const changed = currencyCode !== code;
    currencyCode = code;
    try { storage?.setItem(PREFERENCE_KEY, JSON.stringify({code, expiresAt:now() + DAY})); } catch { /* Storage denial must not disable a choice. */ }
    render();
    if (changed) document.dispatchEvent(new document.defaultView.CustomEvent('adasatee:currency-change', {detail:{currencyCode}}));
  }
  document.addEventListener('change', event => {
    if (event.target.matches?.('[data-adasatee-currency-select]')) setCurrency(event.target.value);
  });
  render();
  return Object.freeze({setCurrency, get currencyCode() { return snapshot ? currencyCode : 'KWD'; }, get snapshot() { return snapshot; }});
}

if (typeof document !== 'undefined' && document.getElementById('adasatee-currency-config')) {
  let storage;
  try { storage = window.localStorage; } catch { /* Private browsing may deny the storage getter itself. */ }
  startCurrencyDisplay({document, storage});
}
